import { CODE_FIELDS, codePickBody, codePicksFromRecord } from "./codePicks";
import { defaultDate, firstOfMonth, monthOf, orNull } from "./format";
import type {
  CodeSetName,
  Headline,
  HeadlineCreate,
  LogKind,
  Lookups,
  Nomination,
  NominationCreate,
  Note,
  NoteCreate,
  PricePoint,
  PricePointCreate,
  PublisherView,
  PublisherViewCreate,
  TabKey,
  Values,
} from "./types";

export interface Option {
  value: string;
  label: string;
}

type Dynamic<T> = T | ((values: Values) => T);

export interface FieldDef {
  key: string;
  label: Dynamic<string>;
  kind: "date" | "month" | "number" | "text" | "url" | "textarea" | "select" | "codePick";
  span: 3 | 4 | 6 | 8 | 12;
  required?: Dynamic<boolean>;
  disabled?: (values: Values) => boolean;
  options?: (lookups: Lookups) => Option[];
  emptyOption?: string;
  min?: Dynamic<number>;
  max?: Dynamic<number>;
  step?: string;
  maxLength?: number;
  placeholder?: string;
}

export interface TabConfig {
  key: TabKey;
  label: string;
  path: string;
  description: string;
  help: string;
  saveLabel: string;
  voidable: boolean;
  fields: FieldDef[];
  defaults: (month: string, lookups: Lookups) => Values;
  toBody: (values: Values, month: string, resinFamilyCode: string) => unknown;
  toPatchBody?: (values: Values) => unknown;
  fromRecord: (record: unknown) => Values;
  recordId: (record: unknown) => number | undefined;
  savedMonth: (values: Values, month: string) => string;
  validate?: (values: Values) => string | null;
}

export function resolve<T>(value: Dynamic<T> | undefined, values: Values): T | undefined {
  return typeof value === "function" ? (value as (v: Values) => T)(values) : value;
}

function codeOptions(set: CodeSetName) {
  return (lookups: Lookups): Option[] =>
    lookups.codes[set].map((c) => ({ value: c.code ?? "", label: c.displayName ?? c.code ?? "" }));
}

function text(value: string | null | undefined): string {
  return value ?? "";
}

function num(value: number | null | undefined): string {
  return value === null || value === undefined ? "" : String(value);
}

const isChange = (v: Values) => v.measureCode === "Change";
const isAnnounced = (v: Values) => v.announcementStatusCode === "Announced";

const CODE_PICK_FIELDS: FieldDef[] = CODE_FIELDS.map((spec) => ({
  key: spec.key, label: spec.key, kind: "codePick", span: 4,
}));

const priceTab: TabConfig = {
  key: "price",
  label: "Price Point",
  path: "consultant-cockpit/price-points",
  description:
    "What happened: an actual price someone saw or paid in the market, spot or contract. " +
    "Shown as dots in Prices in Context.",
  help: "Non-client observations only. Original price and delivery basis are retained.",
  saveLabel: "Save Price Point",
  voidable: true,
  fields: [
    ...CODE_PICK_FIELDS,
    { key: "heardDate", label: "Date heard", kind: "date", span: 4, required: true },
    {
      key: "priceUsdPerLb", label: "Price · $/lb", kind: "number", span: 4, required: true,
      min: 0.001, max: 10, step: "any", placeholder: "1.21",
    },
    {
      key: "priceTypeCode", label: "Price type", kind: "select", span: 4, required: true,
      options: codeOptions("PriceType"), emptyOption: "Spot or contract",
    },
    { key: "location", label: "Location", kind: "text", span: 4, required: true, maxLength: 200 },
    {
      key: "priceBasisCode", label: "Delivery basis", kind: "select", span: 4, required: true,
      options: codeOptions("CockpitPriceBasis"), emptyOption: "Select basis",
    },
    {
      key: "sourceCode", label: "Non-client source", kind: "select", span: 4, required: true,
      options: codeOptions("CockpitPriceSource"), emptyOption: "Select source",
    },
    {
      key: "sourceReference", label: "Source reference", kind: "text", span: 4, required: true,
      maxLength: 200,
    },
    {
      key: "gradeConfirmationCode", label: "Grade applicability", kind: "select", span: 4,
      required: true, options: codeOptions("GradeConfirmation"),
    },
    { key: "notes", label: "Context", kind: "textarea", span: 12, maxLength: 2000 },
  ],
  defaults: (month) => ({ heardDate: defaultDate(month), gradeConfirmationCode: "Unconfirmed" }),
  toBody: (v, _month, resinFamilyCode): PricePointCreate => ({
    resinFamilyCode,
    ...codePickBody(v),
    heardDate: v.heardDate,
    priceUsdPerLb: Number(v.priceUsdPerLb),
    priceTypeCode: v.priceTypeCode,
    location: v.location.trim(),
    priceBasisCode: v.priceBasisCode,
    sourceCode: v.sourceCode,
    sourceReference: v.sourceReference.trim(),
    gradeConfirmationCode: v.gradeConfirmationCode,
    notes: orNull(v.notes),
  }),
  fromRecord: (record) => {
    const r = record as PricePoint;
    return {
      heardDate: r.heardDate,
      priceUsdPerLb: num(r.priceUsdPerLb),
      priceTypeCode: r.priceTypeCode,
      location: r.location,
      priceBasisCode: r.priceBasisCode,
      sourceCode: r.sourceCode,
      sourceReference: r.sourceReference,
      gradeConfirmationCode: r.gradeConfirmationCode,
      notes: text(r.notes),
      ...codePicksFromRecord(r),
    };
  },
  recordId: (record) => (record as PricePoint).cockpitPricePointId,
  savedMonth: (v) => monthOf(v.heardDate),
};

const nominationTab: TabConfig = {
  key: "nomination",
  label: "Nomination",
  path: "consultant-cockpit/nominations",
  description:
    "What suppliers want: a supplier's announced price change for the month, usually from a " +
    "customer letter. Drives Who Has Announced? and the check on your call.",
  help:
    "Enter the cumulative nomination for this month. A later update replaces the displayed " +
    "total; it is not added again.",
  saveLabel: "Save Nomination",
  voidable: true,
  fields: [
    ...CODE_PICK_FIELDS,
    {
      key: "supplierId", label: "Supplier", kind: "select", span: 4, required: true,
      options: (l) =>
        l.roster
          .filter((s) => s.isTracked)
          .map((s) => ({ value: String(s.supplierId), label: s.supplierName ?? "" })),
      emptyOption: "Select a roster supplier",
    },
    { key: "effectiveMonth", label: "Effective month", kind: "month", span: 4, required: true },
    { key: "checkDate", label: "Announcement / check date", kind: "date", span: 4, required: true },
    {
      key: "announcementStatusCode", label: "Status", kind: "select", span: 4, required: true,
      options: codeOptions("AnnouncementStatus"),
    },
    {
      key: "amountCentsPerLb", label: "Total nominated change this month · ¢/lb", kind: "number",
      span: 4, required: isAnnounced, disabled: (v) => !isAnnounced(v), min: -100, max: 100,
      step: "any", placeholder: "+5, 0 or -3",
    },
    {
      key: "sourceReference", label: "Source / confirmation reference", kind: "text", span: 4,
      required: true, maxLength: 200,
    },
    {
      key: "sourceUrl", label: "Source link (optional)", kind: "url", span: 8, maxLength: 1000,
      placeholder: "https://…",
    },
    { key: "notes", label: "Terms / what changed", kind: "textarea", span: 12, maxLength: 2000 },
  ],
  defaults: (month, lookups) => ({
    supplierId: String(lookups.roster.find((s) => s.isTracked)?.supplierId ?? ""),
    effectiveMonth: month,
    checkDate: defaultDate(month),
    announcementStatusCode: "Announced",
  }),
  toBody: (v, _month, resinFamilyCode): NominationCreate => ({
    resinFamilyCode,
    ...codePickBody(v),
    supplierId: Number(v.supplierId),
    effectiveMonth: firstOfMonth(v.effectiveMonth),
    checkDate: v.checkDate,
    announcementStatusCode: v.announcementStatusCode as NominationCreate["announcementStatusCode"],
    amountCentsPerLb: isAnnounced(v) ? Number(v.amountCentsPerLb) : null,
    sourceReference: v.sourceReference.trim(),
    sourceUrl: orNull(v.sourceUrl),
    notes: orNull(v.notes),
  }),
  fromRecord: (record) => {
    const r = record as Nomination;
    return {
      supplierId: String(r.supplierId),
      effectiveMonth: monthOf(r.effectiveMonth),
      checkDate: r.checkDate,
      announcementStatusCode: r.announcementStatusCode,
      amountCentsPerLb: num(r.amountCentsPerLb),
      sourceReference: r.sourceReference,
      sourceUrl: text(r.sourceUrl),
      notes: text(r.notes),
      ...codePicksFromRecord(r),
    };
  },
  recordId: (record) => (record as Nomination).cockpitNominationId,
  savedMonth: (v) => v.effectiveMonth,
};

const headlineTab: TabConfig = {
  key: "headline",
  label: "Headline",
  path: "consultant-cockpit/headlines",
  description:
    "What the news says: a market news item with its publisher and date. " +
    "Listed and searchable in Market Intelligence.",
  help: "Sources stay attributed. Logging a view does not turn it into your call.",
  saveLabel: "Save Headline",
  voidable: true,
  fields: [
    ...CODE_PICK_FIELDS,
    { key: "title", label: "Headline", kind: "text", span: 4, required: true, maxLength: 300 },
    {
      key: "publisherName", label: "Publication / source", kind: "text", span: 4, required: true,
      maxLength: 100,
    },
    { key: "publishedDate", label: "Published / received date", kind: "date", span: 4, required: true },
    {
      key: "regionCode", label: "Region", kind: "select", span: 4,
      options: codeOptions("HeadlineRegion"), emptyOption: "Select region",
    },
    {
      key: "sourceUrl", label: "Article or email link (optional)", kind: "url", span: 4,
      maxLength: 1000, placeholder: "https://…",
    },
    { key: "notes", label: "My read / why it matters", kind: "textarea", span: 12, maxLength: 2000 },
  ],
  defaults: (month) => ({ publishedDate: defaultDate(month) }),
  toBody: (v, _month, resinFamilyCode): HeadlineCreate => ({
    resinFamilyCode,
    ...codePickBody(v),
    title: v.title.trim(),
    publisherName: v.publisherName.trim(),
    publishedDate: v.publishedDate,
    sourceUrl: orNull(v.sourceUrl),
    regionCode: orNull(v.regionCode),
    notes: orNull(v.notes),
  }),
  fromRecord: (record) => {
    const r = record as Headline;
    return {
      title: r.title,
      publisherName: r.publisherName,
      publishedDate: r.publishedDate,
      sourceUrl: text(r.sourceUrl),
      regionCode: text(r.regionCode),
      notes: text(r.notes),
      ...codePicksFromRecord(r),
    };
  },
  recordId: (record) => (record as Headline).marketHeadlineId,
  savedMonth: (v) => monthOf(v.publishedDate),
};

const unit = (v: Values) => (isChange(v) ? "¢/lb" : "$/lb");

const publisherTab: TabConfig = {
  key: "publisher",
  label: "Publisher View",
  path: "consultant-cockpit/forecasts",
  description:
    "What publishers expect: a price-reporting publisher's expected price level ($/lb) or " +
    "monthly change (¢/lb). Shown with the spread across publishers in Market Intelligence.",
  help: "Sources stay attributed. Logging a view does not turn it into your call.",
  saveLabel: "Save Publisher View",
  voidable: true,
  fields: [
    ...CODE_PICK_FIELDS,
    { key: "publisherName", label: "Publisher", kind: "text", span: 4, required: true, maxLength: 100 },
    {
      key: "measureCode", label: "Expectation type", kind: "select", span: 4, required: true,
      options: codeOptions("ForecastMeasure"),
    },
    { key: "forecastMonth", label: "Forecast month", kind: "month", span: 4, required: true },
    { key: "asOfDate", label: "As-of date", kind: "date", span: 4, required: true },
    {
      key: "lowValue", label: (v) => `Low / point estimate · ${unit(v)}`, kind: "number", span: 4,
      required: true, min: (v) => (isChange(v) ? -100 : 0), max: (v) => (isChange(v) ? 100 : 10),
      step: "any",
    },
    {
      key: "highValue", label: (v) => `High · ${unit(v)}`, kind: "number", span: 4, required: true,
      min: (v) => (isChange(v) ? -100 : 0), max: (v) => (isChange(v) ? 100 : 10), step: "any",
    },
    {
      key: "basisText", label: "Grade / location / basis", kind: "text", span: 4, required: true,
      maxLength: 200,
    },
    {
      key: "sourceReference", label: "Source reference", kind: "text", span: 4, required: true,
      maxLength: 200,
    },
    {
      key: "sourceUrl", label: "Source link (optional)", kind: "url", span: 12, maxLength: 1000,
      placeholder: "https://…",
    },
    {
      key: "notes", label: "Publisher rationale / my qualification", kind: "textarea", span: 12,
      maxLength: 2000,
    },
  ],
  defaults: (month) => ({ measureCode: "Level", forecastMonth: month, asOfDate: defaultDate(month) }),
  toBody: (v, _month, resinFamilyCode): PublisherViewCreate => ({
    resinFamilyCode,
    ...codePickBody(v),
    publisherName: v.publisherName.trim(),
    measureCode: v.measureCode as PublisherViewCreate["measureCode"],
    lowValue: Number(v.lowValue),
    highValue: Number(v.highValue),
    currencyCode: "USD",
    forecastMonth: firstOfMonth(v.forecastMonth),
    asOfDate: v.asOfDate,
    basisText: v.basisText.trim(),
    sourceReference: v.sourceReference.trim(),
    sourceUrl: orNull(v.sourceUrl),
    notes: orNull(v.notes),
  }),
  fromRecord: (record) => {
    const r = record as PublisherView;
    return {
      publisherName: r.publisherName,
      measureCode: r.measureCode,
      forecastMonth: monthOf(r.forecastMonth),
      asOfDate: r.asOfDate,
      lowValue: num(r.lowValue),
      highValue: num(r.highValue),
      basisText: r.basisText,
      sourceReference: r.sourceReference,
      sourceUrl: text(r.sourceUrl),
      notes: text(r.notes),
      ...codePicksFromRecord(r),
    };
  },
  recordId: (record) => (record as PublisherView).publisherForecastId,
  savedMonth: (v) => v.forecastMonth,
  validate: (v) =>
    Number(v.highValue) < Number(v.lowValue)
      ? "The high estimate must be at least the low estimate."
      : null,
};

const noteTab: TabConfig = {
  key: "note",
  label: "Note",
  path: "consultant-cockpit/notes",
  description:
    "What you think: your own observation for the month. " +
    "Listed under Consultant Notes in Market Intelligence.",
  help: "A working note for the selected month. It is not evidence and not a market assertion.",
  saveLabel: "Save Note",
  voidable: false,
  fields: [
    ...CODE_PICK_FIELDS,
    { key: "body", label: "Monthly note", kind: "textarea", span: 12, required: true, maxLength: 5000 },
  ],
  defaults: () => ({}),
  toBody: (v, month, resinFamilyCode): NoteCreate => ({
    noteMonth: firstOfMonth(month),
    body: v.body.trim(),
    resinFamilyCode,
    ...codePickBody(v),
  }),
  toPatchBody: (v) => ({ body: v.body.trim(), ...codePickBody(v) }),
  fromRecord: (record) => {
    const r = record as Note;
    return { body: r.body, ...codePicksFromRecord(r) };
  },
  recordId: (record) => (record as Note).consultantNoteId,
  savedMonth: (_v, month) => month,
};

const rosterTab: TabConfig = {
  key: "roster",
  label: "Supplier Roster",
  path: "consultant-cockpit/roster",
  description:
    "Who you track: each tracked supplier gets a card in Who Has Announced?, and can be " +
    "picked when you log a nomination.",
  help: "Tracked suppliers without a monthly record remain “Not yet checked”.",
  saveLabel: "Save to Roster",
  voidable: false,
  fields: [
    { key: "supplierName", label: "Supplier name", kind: "text", span: 6, required: true, maxLength: 100 },
    {
      key: "isTracked", label: "Track this supplier", kind: "select", span: 6, required: true,
      options: () => [
        { value: "yes", label: "Yes" },
        { value: "no", label: "No" },
      ],
    },
  ],
  defaults: () => ({ isTracked: "yes" }),
  toBody: (v) => ({ supplierName: v.supplierName.trim(), isTracked: v.isTracked === "yes" }),
  toPatchBody: (v) => ({ isTracked: v.isTracked === "yes" }),
  fromRecord: () => ({}),
  recordId: () => undefined,
  savedMonth: (_v, month) => month,
};

export const TABS: TabConfig[] = [
  priceTab,
  nominationTab,
  headlineTab,
  publisherTab,
  noteTab,
  rosterTab,
];

export const TAB_BY_KEY = Object.fromEntries(TABS.map((t) => [t.key, t])) as Record<TabKey, TabConfig>;

export const TAB_BY_KIND: Record<LogKind, TabKey> = {
  PricePoint: "price",
  Nomination: "nomination",
  Headline: "headline",
  Forecast: "publisher",
  Note: "note",
};

export const KIND_LABEL: Record<LogKind, string> = {
  PricePoint: "Price point",
  Nomination: "Nomination",
  Headline: "Headline",
  Forecast: "Publisher view",
  Note: "Note",
};
